// ============================================================================
// MOCK CALCULATION ENGINE — SIMPLIFIED, PURE, SYNCHRONOUS.
// Not the production estimating formula set. It exists so the prototype's
// numbers move believably when an estimator edits a line. The real engine
// will run server-side and return the same CalcResult shape.
// ============================================================================

import { SPRAY_TOOLS } from "../mock/config.mock";

const sum = (arr, fn) => arr.reduce((acc, x) => acc + (fn ? fn(x) : x), 0);
const ceilPos = (n) => (n > 0 ? Math.ceil(n - 1e-9) : 0);

/** Builds O(1) lookup maps for a config. Cache-friendly: call once per config. */
export const indexConfig = (config) => ({
  surfaces: Object.fromEntries(config.surfaces.map((s) => [s.id, s])),
  products: Object.fromEntries(config.products.map((p) => [p.id, p])),
  bands: Object.fromEntries(config.accessBands.map((b) => [b.id, b])),
  sspc: Object.fromEntries(config.finishingMasking.sspc.map((s) => [s.id, s])),
  finishLevels: Object.fromEntries(config.finishingMasking.finishLevels.map((f) => [f.id, f])),
});

/** Brand Mode swaps a product for its other-brand equivalent when one exists. */
export const resolveProductId = (productId, brandMode, products) => {
  const p = products[productId];
  if (!p || brandMode === "MIX") return productId;
  const isSw = p.manufacturer === "Sherwin-Williams";
  const isBm = p.manufacturer === "Benjamin Moore";
  if ((brandMode === "SW" && isBm) || (brandMode === "BM" && isSw)) {
    return p.equivalentId && products[p.equivalentId] ? p.equivalentId : productId;
  }
  return productId;
};

export const accessForHeight = (heightFt, bands) => {
  if (heightFt == null) return "GROUND";
  return (bands.find((b) => heightFt <= b.maxFt) || bands[bands.length - 1]).id;
};

const primerByRule = (rule, condition) =>
  rule === "ALWAYS" || (rule === "NEW" && condition === "New construction");

/**
 * Values a line would have if nobody touched it. The Takeoff grid compares
 * against these to paint "overridden" cells blue and offer a reset.
 */
export const getLineDefaults = (line, config, project, idx = indexConfig(config)) => {
  const surface = idx.surfaces[line.surfaceId];
  if (!surface) return {};
  const condition = project.conditionDefault;
  const specProduct = line.specCode && config.specCodes[line.specCode];
  return {
    coats: surface.defaultCoats,
    primer: !!surface.defaultPrimerProductId && primerByRule(surface.primerRule, condition),
    topcoat: true,
    sspcPrep: surface.group === "Metals" ? "SP2" : "None",
    subbed: surface.subbed,
    access: accessForHeight(line.heightFt, config.accessBands),
    finishLevel: /level 5/i.test(line.rawName || "") ? "L5" : surface.finishLevel,
    condition,
    tool: surface.defaultTool,
    finishProductId: specProduct || surface.defaultFinishProductId,
    primerProductId: surface.defaultPrimerProductId,
  };
};

/** Fields of a line the grid validates; returns { field: message }. */
export const validateLine = (line, idx) => {
  const errors = {};
  if (!idx.surfaces[line.surfaceId]) errors.surfaceId = "Pick a surface";
  if (!(Number(line.nativeQty) > 0)) errors.nativeQty = "Qty must be greater than 0";
  const c = Number(line.coats);
  if (!Number.isInteger(c) || c < 0 || c > 5) errors.coats = "Coats must be 0–5";
  return errors;
};

/**
 * One takeoff line → quantities, hours and $.
 * Every intermediate value is returned so the Line Inspector can explain it.
 */
export const calcLine = (line, ctx) => {
  const { config, idx, project } = ctx;
  const ga = config.globalAssumptions;
  const fm = config.finishingMasking;
  const surface = idx.surfaces[line.surfaceId];
  const errors = validateLine(line, idx);
  const valid = Object.keys(errors).length === 0;

  const base = {
    id: line.id, valid, errors, surface, side: surface?.side || "INT",
    sfQty: 0, finishGal: 0, primerGal: 0, fieldHrs: 0, cutHrs: 0, prepHrs: 0,
    finishHrs: 0, maskHrs: 0, totalHrs: 0, laborCost: 0, subCost: 0, materialCost: 0,
    lineTotal: 0, maskedSf: 0,
  };
  if (!valid) return base;

  const sfQty = line.unit === "SF" ? Number(line.nativeQty) : Number(line.nativeQty) * surface.sfPerUnit;
  const difficulty = Number(project.difficultyFactor) || 1;

  if (line.subbed) {
    const subRate = surface.subRatePerSf || 3.5;
    return { ...base, sfQty, subRate, subCost: sfQty * subRate, lineTotal: sfQty * subRate };
  }

  const finishProductId = resolveProductId(line.finishProductId, project.brandMode, idx.products);
  const primerProductId = resolveProductId(line.primerProductId, project.brandMode, idx.products);
  const finishProduct = idx.products[finishProductId] || null;
  const primerProduct = line.primer ? idx.products[primerProductId] || null : null;

  const finishCoats = line.topcoat ? Number(line.coats) : 0;
  const primerPasses = primerProduct ? 1 : 0;
  const passes = finishCoats + primerPasses;
  const toolWaste = ga.toolWaste[line.tool] ?? 0.1;
  const accessMult = idx.bands[line.access]?.mult ?? 1;

  const coverage = finishProduct ? finishProduct.coverage * surface.factor : 0;
  const finishGal = finishProduct && finishCoats > 0 ? ((sfQty * finishCoats) / coverage) * (1 + toolWaste) : 0;
  const primerCoverage = primerProduct?.coverage || 0;
  const primerGal = primerProduct ? (sfQty / primerCoverage) * (1 + toolWaste) : 0;

  const prodTool = surface.productivity[line.tool] || surface.productivity.Roller;
  const prodBrush = surface.productivity.Brush;
  const cutPct = surface.cutPct;
  const fieldHrs = cutPct >= 1 ? 0 : ((sfQty * (1 - cutPct) * passes) / prodTool) * accessMult;
  const cutHrs = ((sfQty * cutPct * passes) / prodBrush) * accessMult;

  const conditionPrep = fm.conditionPrep[line.condition] ?? 0;
  const sspcAdd = idx.sspc[line.sspcPrep]?.add ?? 0;
  const prepHrs = (sfQty / 100) * (conditionPrep + sspcAdd);
  const finishHrs = (sfQty / 100) * (idx.finishLevels[line.finishLevel]?.add ?? 0);
  const maskRate = fm.maskRates[line.tool] ?? 0;
  const maskHrs = (sfQty / 100) * maskRate;

  const rawHrs = fieldHrs + cutHrs + prepHrs + finishHrs + maskHrs;
  const totalHrs = rawHrs * difficulty;
  const finishingShare = finishHrs * difficulty;
  const laborCost = (totalHrs - finishingShare) * ga.laborRatePainting + finishingShare * ga.laborRateFinishing;
  const materialCost = finishGal * (finishProduct?.pricePerGal || 0) + primerGal * (primerProduct?.pricePerGal || 0);

  return {
    ...base,
    sfQty, finishProduct, primerProduct, finishProductId, primerProductId,
    finishCoats, primerPasses, passes, toolWaste, accessMult, coverage, primerCoverage,
    finishGal, primerGal, prodTool, prodBrush, cutPct,
    conditionPrep, sspcAdd, maskRate, difficulty,
    fieldHrs, cutHrs, prepHrs, finishHrs, maskHrs, rawHrs, totalHrs,
    laborCost, materialCost, lineTotal: laborCost + materialCost,
    maskedSf: SPRAY_TOOLS.includes(line.tool) ? sfQty : 0,
  };
};

/** Splits a gallon need into 5-gal / gallon / quart containers (cheapest-first rounding). */
export const purchaseBreakdown = (gal, product, sizes) => {
  if (!(gal > 0) || !product) return { fiveGal: 0, gallon: 0, quart: 0, purchased: 0, cost: 0 };
  let fiveGal = Math.floor(gal / sizes.fiveGal);
  let rest = gal - fiveGal * sizes.fiveGal;
  let gallon = Math.floor(rest / sizes.gallon);
  rest -= gallon * sizes.gallon;
  let quart = ceilPos(rest / sizes.quart);
  if (quart >= 4) { gallon += 1; quart = 0; }
  if (gallon >= 4) { fiveGal += 1; gallon = 0; quart = 0; }
  const purchased = fiveGal * sizes.fiveGal + gallon * sizes.gallon + quart * sizes.quart;
  const cost = fiveGal * product.pricePer5Gal + gallon * product.pricePerGal + quart * product.pricePerQt;
  return { fiveGal, gallon, quart, purchased, cost };
};

const bandDays = (hrs, crew, hpd) => (crew > 0 ? hrs / (crew * hpd) : 0);

const buildEquipment = (results, lines, config, project, overrides) => {
  const hpd = config.globalAssumptions.hoursPerDay;
  const intCrew = Number(project.interiorCrew) || 1;
  const extCrew = Number(project.exteriorCrew) || 1;

  const bands = config.accessBands.map((b) => {
    const rs = results.filter((r, i) => lines[i].access === b.id);
    const intHrs = sum(rs.filter((r) => r.side === "INT"), (r) => r.totalHrs);
    const extHrs = sum(rs.filter((r) => r.side === "EXT"), (r) => r.totalHrs);
    return {
      id: b.id, label: b.label, mult: b.mult, hrs: intHrs + extHrs, intHrs, extHrs,
      daysInt: bandDays(intHrs, intCrew, hpd), daysExt: bandDays(extHrs, extCrew, hpd),
    };
  });
  const bandById = Object.fromEntries(bands.map((b) => [b.id, b]));
  const toolHrs = (tool) => sum(results.filter((r, i) => lines[i].tool === tool && !lines[i].subbed), (r) => r.totalHrs);

  const computed = config.equipmentCatalog.map((eq) => {
    const d = eq.driver;
    let qty = 0;
    let basisNote = "";
    if (d.mode === "used") { qty = bandById[d.band]?.hrs > 0 ? 1 : 0; basisNote = `${bandById[d.band]?.label} work present`; }
    if (d.mode === "weeks") {
      const days = (bandById[d.band]?.daysInt || 0) + (bandById[d.band]?.daysExt || 0);
      qty = ceilPos(days / 5);
      basisNote = `${days.toFixed(1)} crew-days in ${bandById[d.band]?.label}`;
    }
    if (d.mode === "toolDays") { const h = toolHrs(d.tool); qty = ceilPos(h / hpd); basisNote = `${h.toFixed(1)} ${d.tool} hrs`; }
    if (d.mode === "fixed") { qty = d.qty; basisNote = "Allowance"; }
    return { ...eq, computedQty: qty, basisNote };
  });
  // Lift trips depend on which lifts ended up on site.
  const liftsOnSite = computed.filter((e) => e.driver.mode === "weeks" && (overrides[e.id] ?? e.computedQty) > 0).length;
  const items = computed.map((e) => {
    const computedQty = e.driver.mode === "liftTrips" ? liftsOnSite * 2 : e.computedQty;
    const basisNote = e.driver.mode === "liftTrips" ? `${liftsOnSite} lift(s) × deliver + pickup` : e.basisNote;
    const qty = overrides[e.id] ?? computedQty;
    return { ...e, computedQty, basisNote, qty, overridden: overrides[e.id] != null, cost: qty * e.rate };
  });

  const intHrs = sum(results.filter((r) => r.side === "INT"), (r) => r.totalHrs);
  const extHrs = sum(results.filter((r) => r.side === "EXT"), (r) => r.totalHrs);
  const intDays = bandDays(intHrs, intCrew, hpd);
  const extDays = bandDays(extHrs, extCrew, hpd);
  return {
    bands, items, total: sum(items, (i) => i.cost),
    crew: { intCrew, extCrew, intHrs, extHrs, intDays, extDays },
    durationDays: ceilPos(intDays) + ceilPos(extDays),
  };
};

const gcQty = (basis, days, ga, defaultQty) => {
  if (basis === "Day") return days;
  if (basis === "Week") return ceilPos(days / 5);
  if (basis === "Month") return Math.max(1, ceilPos(days / ga.workDaysPerMonth));
  if (basis === "Each") return defaultQty || 1;
  return 1;
};

const buildGc = (config, durationDays, overrides, templateId) => {
  const ga = config.globalAssumptions;
  const tpl = config.gcTemplates.find((t) => t.id === templateId) || config.gcTemplates[0];
  const units = { Day: "days", Week: "weeks", Month: "months", Each: "ea", Lump: "ls" };
  const items = tpl.items.map((it) => {
    const computedQty = gcQty(it.basis, durationDays, ga, it.defaultQty);
    const qty = overrides[it.id] ?? computedQty;
    return { ...it, computedQty, qty, units: units[it.basis], overridden: overrides[it.id] != null, cost: qty * it.rate };
  });
  return { templateId: tpl.id, templateName: tpl.name, items, total: sum(items, (i) => i.cost) };
};

const buildMaterials = (results, lines, config, project, idx) => {
  const ga = config.globalAssumptions;
  const fm = config.finishingMasking;
  const sizes = ga.containerSizes;
  const agg = (key, productKey) => {
    const map = {};
    results.forEach((r) => {
      const p = r[productKey];
      if (!p || !(r[key] > 0)) return;
      map[p.id] = map[p.id] || { product: p, gal: 0 };
      map[p.id].gal += r[key];
    });
    return Object.values(map).map(({ product, gal }) => ({
      id: product.id,
      product: product.id,
      description: `${product.manufacturer} ${product.line} – ${product.sheen}`,
      qtyNeeded: gal,
      ...purchaseBreakdown(gal, product, sizes),
    }));
  };
  const paints = agg("finishGal", "finishProduct");
  const primers = agg("primerGal", "primerProduct");
  const paintCost = sum(paints, (p) => p.cost) + sum(primers, (p) => p.cost);

  const l5Sf = sum(results.filter((r, i) => lines[i].finishLevel === "L5"), (r) => r.sfQty);
  const f = fm.finishing;
  const compoundGal = (l5Sf / 100) * f.compoundGalPer100Sf;
  const finishing = l5Sf > 0 ? [
    { id: "compound", product: "Joint compound", description: `All-purpose, ${f.compoundBucketGal} gal bucket – Level 5 skim (${l5Sf.toFixed(0)} SF)`, qtyNeeded: compoundGal, purchased: ceilPos(compoundGal / f.compoundBucketGal), unitLabel: "buckets", cost: ceilPos(compoundGal / f.compoundBucketGal) * f.compoundBucketPrice },
    { id: "joint-tape", product: "Joint tape", description: "Paper joint tape, 500 ft roll", qtyNeeded: l5Sf / f.tapeSfPerRoll, purchased: ceilPos(l5Sf / f.tapeSfPerRoll), unitLabel: "rolls", cost: ceilPos(l5Sf / f.tapeSfPerRoll) * f.tapeRollPrice },
  ] : [];

  const caulkLf = sum(results.filter((r, i) => ["Trim", "Doors & Frames"].includes(r.surface?.group) && !lines[i].subbed), (r, ) => r.sfQty * 2);
  const tubes = ceilPos((caulkLf / 100) * fm.caulk.tubesPer100Lf);
  const caulk = tubes > 0 ? [{ id: "caulk", product: "Painter's caulk", description: `Siliconized acrylic, 10.1 oz – ${caulkLf.toFixed(0)} LF of joints`, qtyNeeded: tubes, purchased: tubes, unitLabel: "tubes", cost: tubes * fm.caulk.pricePerTube }] : [];

  const maskedSf = sum(results, (r) => r.maskedSf);
  const m = fm.masking;
  const masking = maskedSf > 0 ? [
    { id: "mask-tape", product: "Masking tape", description: `2" painter's tape – ${maskedSf.toFixed(0)} SF sprayed`, qtyNeeded: maskedSf / m.tapeSfPerRoll, purchased: ceilPos(maskedSf / m.tapeSfPerRoll), unitLabel: "rolls", cost: ceilPos(maskedSf / m.tapeSfPerRoll) * m.tapePrice },
    { id: "plastic", product: "Masking plastic", description: "Pre-taped plastic, 9 ft × 90 ft", qtyNeeded: maskedSf / m.plasticSfPerRoll, purchased: ceilPos(maskedSf / m.plasticSfPerRoll), unitLabel: "rolls", cost: ceilPos(maskedSf / m.plasticSfPerRoll) * m.plasticPrice },
  ] : [];

  const sundriesTotal = paintCost * ga.sundriesPct;
  const sundries = fm.sundriesSplit.map((s) => ({ id: s.key, product: s.label, description: `${(s.share * 100).toFixed(0)}% of sundries allowance`, cost: sundriesTotal * s.share }));

  const sections = [
    { id: "paint", label: "Paint & Coatings", rows: paints, kind: "paint" },
    { id: "primers", label: "Primers", rows: primers, kind: "paint" },
    { id: "finishing", label: "Finishing (compound, tape)", rows: finishing, kind: "unit" },
    { id: "sundries", label: "Sundries", rows: sundries, kind: "allowance" },
    { id: "caulk", label: "Caulk", rows: caulk, kind: "unit" },
    { id: "masking", label: "Masking", rows: masking, kind: "unit" },
  ].map((s) => ({ ...s, total: sum(s.rows, (r) => r.cost) }));

  const subtotal = sum(sections, (s) => s.total);
  const tax = project.taxExempt ? 0 : subtotal * ga.salesTaxPct;
  return { sections, paintCost, sundriesTotal, subtotal, tax, total: subtotal + tax };
};

const buildWaterfall = (t, config, project) => {
  const ga = config.globalAssumptions;
  const directSubtotal = t.material + t.labor + t.equipment;
  const fieldCost = directSubtotal + t.gc;
  const overhead = fieldCost * ga.overheadPct;
  const selfPerformOh = fieldCost + overhead;
  const subs = t.subCost * (1 + ga.subMarkupPct);
  const costBeforeProfit = selfPerformOh + subs;
  const contingency = costBeforeProfit * (Number(project.contingencyPct) || 0);
  const costInclContingency = costBeforeProfit + contingency;
  const bonds = project.bondRequired ? costInclContingency * ga.bondPct : 0;
  const insurance = Number(project.projectInsurance) || 0;
  const profit = (costInclContingency + bonds + insurance) * ga.profitPct;
  const total = costInclContingency + bonds + insurance + profit;

  const steps = [
    { key: "material", label: "Material", value: t.material, type: "add" },
    { key: "labor", label: "Labor", value: t.labor, type: "add" },
    { key: "equipment", label: "Equipment", value: t.equipment, type: "add" },
    { key: "direct", label: "Direct Cost Subtotal", value: directSubtotal, type: "subtotal" },
    { key: "gc", label: "General Conditions", value: t.gc, type: "add" },
    { key: "field", label: "Field Cost Subtotal", value: fieldCost, type: "subtotal" },
    { key: "overhead", label: `Company Overhead (${(ga.overheadPct * 100).toFixed(0)}%)`, value: overhead, type: "add" },
    { key: "selfPerform", label: "Self-perform + OH", value: selfPerformOh, type: "subtotal" },
    { key: "subs", label: `Subcontractors (incl. ${(ga.subMarkupPct * 100).toFixed(0)}% markup)`, value: subs, type: "add" },
    { key: "beforeProfit", label: "Cost Before Profit", value: costBeforeProfit, type: "subtotal" },
    { key: "contingency", label: `Contingency (${((Number(project.contingencyPct) || 0) * 100).toFixed(1)}%)`, value: contingency, type: "add" },
    { key: "inclContingency", label: "Cost incl. Contingency", value: costInclContingency, type: "subtotal" },
    { key: "bonds", label: `Bonds & Insurance (${(ga.bondPct * 100).toFixed(0)}%)`, value: bonds, type: "add", muted: !project.bondRequired },
    { key: "insurance", label: "Project Insurance", value: insurance, type: "add" },
    { key: "profit", label: `Profit / Markup (${(ga.profitPct * 100).toFixed(0)}%)`, value: profit, type: "add" },
    { key: "total", label: "TOTAL BID PRICE", value: total, type: "total" },
  ];
  return { steps, total, directSubtotal, fieldCost, overhead, subs, contingency, bonds, insurance, profit };
};

const buildBoq = (results, lines, idx, bidTotal) => {
  const groups = {};
  results.forEach((r, i) => {
    if (!r.valid) return;
    const line = lines[i];
    const key = line.subbed ? `${line.surfaceId}|SUB` : `${line.surfaceId}|${r.finishProductId}|${r.finishCoats}|${r.primerProduct?.id || ""}`;
    if (!groups[key]) {
      const s = r.surface;
      let description;
      if (line.subbed) description = `Subcontract: ${s.name} (by specialty subcontractor)`;
      else if (r.finishCoats > 0 && r.finishProduct) {
        description = `Supply & apply ${r.finishCoats} coat${r.finishCoats === 1 ? "" : "s"} ${r.finishProduct.line} ${r.finishProduct.sheen} to ${s.name}`;
        if (r.primerProduct) description += `, incl. 1 coat ${r.primerProduct.line}`;
      } else description = `Prime only – ${r.primerProduct?.line || "primer"} to ${s.name}`;
      groups[key] = { key, surfaceName: s.name, description, unit: line.unit, qty: 0, direct: 0, lineCount: 0 };
    }
    groups[key].qty += Number(line.nativeQty);
    groups[key].direct += r.lineTotal;
    groups[key].lineCount += 1;
  });
  const rows = Object.values(groups);
  const directAll = sum(rows, (g) => g.direct);
  return rows.map((g, i) => {
    const amount = directAll > 0 ? (g.direct / directAll) * bidTotal : 0;
    return { ...g, item: `${String(Math.floor(i / 9) + 1).padStart(2, "0")}.${String((i % 9) + 1).padStart(2, "0")}`, amount, rate: g.qty > 0 ? amount / g.qty : 0 };
  });
};

/**
 * Whole-estimate calculation. Pure: same input → same output.
 * @param {{lines: import("../types").EstimateLine[], project: import("../types").ProjectSetup,
 *          equipmentOverrides?: Object, gcOverrides?: Object}} input
 */
export const calculateEstimate = (input, config, idx = indexConfig(config)) => {
  const { lines, project } = input;
  const ctx = { config, idx, project };
  const results = lines.map((l) => calcLine(l, ctx));

  const totals = {
    sfQty: sum(results, (r) => r.sfQty),
    finishGal: sum(results, (r) => r.finishGal),
    primerGal: sum(results, (r) => r.primerGal),
    fieldHrs: sum(results, (r) => r.fieldHrs),
    cutHrs: sum(results, (r) => r.cutHrs),
    prepHrs: sum(results, (r) => r.prepHrs + r.finishHrs),
    maskHrs: sum(results, (r) => r.maskHrs),
    totalHrs: sum(results, (r) => r.totalHrs),
    laborCost: sum(results, (r) => r.laborCost),
    subCost: sum(results, (r) => r.subCost),
    materialCost: sum(results, (r) => r.materialCost),
    lineTotal: sum(results, (r) => r.lineTotal),
    invalidCount: results.filter((r) => !r.valid).length,
  };

  const equipment = buildEquipment(results, lines, config, project, input.equipmentOverrides || {});
  const gc = buildGc(config, equipment.durationDays, input.gcOverrides || {}, project.gcTemplateId || config.defaultGcTemplateId);
  const materials = buildMaterials(results, lines, config, project, idx);
  const waterfall = buildWaterfall(
    { material: materials.total, labor: totals.laborCost, equipment: equipment.total, gc: gc.total, subCost: totals.subCost },
    config,
    project
  );
  const boq = buildBoq(results, lines, idx, waterfall.total);

  const kpis = {
    bidTotal: waterfall.total,
    pricePerSf: totals.sfQty > 0 ? waterfall.total / totals.sfQty : 0,
    paintableSf: totals.sfQty,
    laborHours: totals.totalHrs,
    manDays: totals.totalHrs / config.globalAssumptions.hoursPerDay,
    materialCost: materials.total,
    durationDays: equipment.durationDays,
  };

  return { results, totals, equipment, gc, materials, waterfall, boq, kpis };
};

/** Snapshot stored on history entries / the estimates list. */
export const toTotalsSnapshot = (calc) => ({
  bidTotal: calc.kpis.bidTotal,
  paintableSf: calc.kpis.paintableSf,
  laborHours: calc.kpis.laborHours,
  materialCost: calc.kpis.materialCost,
  durationDays: calc.kpis.durationDays,
});

/** Mapped parse line → editable estimate line with surface defaults applied. */
export const buildEstimateLine = (parsed, surfaceId, config, project, idx = indexConfig(config)) => {
  const surface = idx.surfaces[surfaceId];
  const line = {
    id: parsed.id,
    section: parsed.section,
    rawName: parsed.rawName,
    area: parsed.location,
    surfaceId,
    unit: parsed.unit || surface?.unit || "SF",
    nativeQty: parsed.qty || 0,
    heightFt: parsed.heightFt,
    specCode: parsed.specCode,
  };
  return { ...line, ...getLineDefaults(line, config, project, idx) };
};
